import {
  Currency,
  HowItWorksStep,
  WhyChooseFeature,
  FaqItem,
  Testimonial,
  KitTier,
  KitCategory,
  ServiceTab,
} from "./types";

// Asset base — served from amber-forex public folder via nginx
export const FOREX_ASSET_BASE = "/plus/forex/assets";

// `comingSoon` stays in the row type so the ServiceNavTabs SOON badge + click
// gating keep compiling for future services that aren't ready yet. Set to
// `true` on a row when it should show as "coming soon" in the tab strip.
interface ServiceTabRow {
  id: ServiceTab;
  label: string;
  comingSoon?: boolean;
}

export const SERVICE_TABS: ServiceTabRow[] = [
  { id: "forex-card", label: "Forex Card" },
  { id: "essential-kit", label: "Essential Kit" },
  { id: "flights", label: "Flights" },
  { id: "bank", label: "Bank" },
  { id: "airport-pickup", label: "Airport Pickup" },
  { id: "accommodation", label: "Accommodation" },
];

export const AIRPORT_REDIRECT_URL = "https://amberstudent.hoppa.com/";
export const AIRPORT_REDIRECT_DELAY_MS = 1500;

export const ACCOMMODATION_REDIRECT_URL = "/";
export const ACCOMMODATION_REDIRECT_DELAY_MS = 1500;

export const TAB_TO_SLUG: Partial<Record<ServiceTab, string>> = {
  "forex-card": "forex",
  "essential-kit": "kit",
  accommodation: "accommodation",
  flights: "flights",
  bank: "bank",
};

export const SLUG_TO_TAB: Record<string, ServiceTab> = {
  forex: "forex-card",
  kit: "essential-kit",
  accommodation: "accommodation",
  flights: "flights",
  bank: "bank",
};

export const CROSS_PAGE_META: Record<string, { title_prefix: string; description: string }> = {
  overview: {
    title_prefix: "Amber Plus: Exclusive Student Services, Deals & Perks",
    description:
      "Explore Amber Plus services for students — forex cards, essential kits, flight discounts, UK bank accounts & airport pickup. Exclusive deals & perks.",
  },
  forex: {
    title_prefix: "Forex Cards & Currency Exchange For Students",
    description:
      "Get your forex card or cash delivered in 1–3 days. Live exchange rates, transparent pricing & RBI-compliant process for students moving abroad.",
  },
  kit: {
    title_prefix: "Student Essential Kits: Bedding, Kitchen & Bathroom",
    description:
      "Order your university starter kit — bedding, kitchen & bathroom essentials delivered to your halls on weekdays, with a minimum of 5 days. Trusted by students across the UK.",
  },
  flights: {
    title_prefix: "Student Flight Deals with MakeMyTrip",
    description:
      "Save up to 7%* on student flights with amber × MakeMyTrip. Pre-book flights to your new city — open to all students, no amber booking required.",
  },
  bank: {
    title_prefix: "UK Student Bank Account: Get £40 With Revolut",
    description:
      "Open a Revolut account on arrival in the UK and unlock £40 in rewards. Fully digital — no branch visits, queues or UK credit history needed.",
  },
  accommodation: {
    title_prefix: "Student Accommodation Near Your University",
    description:
      "Find and book safe, budget-friendly student accommodation near your university. Browse 10,000+ properties, private halls & apartments worldwide.",
  },
};

export const SERVICE_MOBILE_REDIRECTS: Partial<Record<ServiceTab, string>> = {};

export const CURRENCIES: Currency[] = [
  { code: "USD", name: "US Dollar", country: "USA", flag: "https://flagcdn.com/w40/us.png" },
  {
    code: "AED",
    name: "UAE Dirham",
    country: "United Arab Emirates",
    flag: "https://flagcdn.com/w40/ae.png",
  },
  { code: "THB", name: "Thai Baht", country: "Thailand", flag: "https://flagcdn.com/w40/th.png" },
  {
    code: "EUR",
    name: "Euro",
    country: "France, Germany, +45 Countries",
    flag: "https://flagcdn.com/w40/eu.png",
  },
  {
    code: "SGD",
    name: "Singapore Dollar",
    country: "Singapore",
    flag: "https://flagcdn.com/w40/sg.png",
  },
  {
    code: "GBP",
    name: "British Pound",
    country: "United Kingdom",
    flag: "https://flagcdn.com/w40/gb.png",
  },
  {
    code: "HKD",
    name: "Hong Kong Dollar",
    country: "Hong Kong",
    flag: "https://flagcdn.com/w40/hk.png",
  },
  {
    code: "CHF",
    name: "Swiss Franc",
    country: "Switzerland",
    flag: "https://flagcdn.com/w40/ch.png",
  },
  {
    code: "SAR",
    name: "Saudi Riyal",
    country: "Saudi Arabia",
    flag: "https://flagcdn.com/w40/sa.png",
  },
  {
    code: "CAD",
    name: "Canadian Dollar",
    country: "Canada",
    flag: "https://flagcdn.com/w40/ca.png",
  },
  {
    code: "ZAR",
    name: "South African Rand",
    country: "South Africa",
    flag: "https://flagcdn.com/w40/za.png",
  },
  {
    code: "AUD",
    name: "Australian Dollar",
    country: "Australia",
    flag: "https://flagcdn.com/w40/au.png",
  },
  { code: "JPY", name: "Japanese Yen", country: "Japan", flag: "https://flagcdn.com/w40/jp.png" },
  { code: "SEK", name: "Swedish Krona", country: "Sweden", flag: "https://flagcdn.com/w40/se.png" },
  {
    code: "NZD",
    name: "New Zealand Dollar",
    country: "New Zealand",
    flag: "https://flagcdn.com/w40/nz.png",
  },
  {
    code: "MYR",
    name: "Malaysian Ringgit",
    country: "Malaysia",
    flag: "https://flagcdn.com/w40/my.png",
  },
  { code: "OMR", name: "Omani Rial", country: "Oman", flag: "https://flagcdn.com/w40/om.png" },
  { code: "QAR", name: "Qatari Rial", country: "Qatar", flag: "https://flagcdn.com/w40/qa.png" },
  {
    code: "BHD",
    name: "Bahraini Dinar",
    country: "Bahrain",
    flag: "https://flagcdn.com/w40/bh.png",
  },
  { code: "KWD", name: "Kuwaiti Dinar", country: "Kuwait", flag: "https://flagcdn.com/w40/kw.png" },
];

export const BUY_TYPES = [
  {
    value: "card" as const,
    label: "Forex Card",
    icon: "https://prod-static-assets.amberstudent.com/images/amber_plus_cross_forex_credit-card.svg",
  },
  {
    value: "currency" as const,
    label: "Forex Cash",
    icon: "https://prod-static-assets.amberstudent.com/images/amber_plus_cross_forex_cash.svg",
  },
];

export const FOREX_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    stepNumber: 1,
    title: "Check Live Rate",
    description: "View real-time exchange rates and choose your preferred currency.",
    iconPath: "check-rate",
  },
  {
    stepNumber: 2,
    title: "Fill In Details",
    description: "Choose card or cash and complete your order in minutes.",
    iconPath: "fill-details",
  },
  {
    stepNumber: 3,
    title: "Receive Cash/Card",
    description: "Receive your forex card or cash at your doorstep",
    iconPath: "receive",
  },
];

export const FOREX_WHY_CHOOSE: WhyChooseFeature[] = [
  {
    title: "Secure & RBI-Compliant",
    description: "Safe transactions and verified documentation at every step.",
    iconPath: "verified",
  },
  {
    title: "Transparent Pricing",
    description: "Clear rate breakdowns with zero hidden fees.",
    iconPath: "transparent",
  },
  {
    title: "Quick Processing",
    description: "Smooth online process and fast order confirmation.",
    iconPath: "quick",
  },
  {
    title: "1–3 Day Delivery",
    description: "Forex card or cash delivered directly to your doorstep.",
    iconPath: "delivery",
  },
];

export const FOREX_FAQS: FaqItem[] = [
  {
    question: "How do I buy forex through Amber?",
    answer:
      "You can submit your forex requirement online by selecting the currency and entering your details. Once submitted, our team will get in touch to guide you through the next steps.",
  },
  {
    question: "How will I know the final rate?",
    answer:
      "You'll see indicative live rates on the platform. The final confirmed rate will be shared by our team when they contact you.",
  },
  {
    question: "What currencies are available?",
    answer:
      "We support major global currencies including USD, EUR, GBP, AUD, CAD, and more. Availability depends on regulatory guidelines and location.",
  },
  {
    question: "How long does delivery take?",
    answer:
      "Once your order is confirmed and processed, forex card or cash is typically delivered within 1–3 working days.",
  },
  {
    question: "Is it safe to share my details?",
    answer:
      "Yes. Your information is handled securely and used only to assist with your forex requirement.",
  },
];

export const FOREX_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Because of the agent's great efforts, I have been able to book a room with the accommodation that I wanted. Would recommend AmberStudent to all future students!",
    name: "Priya Sharma",
    university: "University of Edinburgh",
  },
  {
    quote:
      "AmberStudent is excellent for student accommodation. My agent's support in booking our group space was invaluable. Always updated and responsive. Grateful!",
    name: "Ciarán Murphy",
    university: "University College Dublin",
  },
  {
    quote:
      "Very responsive team. Clear instructions and a successful outcome. Couldn't have asked for a smoother experience booking my forex card.",
    name: "Emily Johnson",
    university: "New York University",
  },
  {
    quote:
      "Absolutely wonderful. The agent was so helpful and supportive and was there through the whole process and made it as easy and stress free as possible.",
    name: "Aoife Kelly",
    university: "Trinity College Dublin",
  },
];

export const KIT_TIERS: KitTier[] = [
  {
    name: "Standard Kit",
    price: "£179",
    description:
      "Comprehensive uni bundle including bedroom, bathroom, kitchen & crockery kits. Everything you need in one delivery.",
    features: [
      "Induction pan set",
      "2 x Person Dining Set",
      "12pc Value Utensil Set",
      "30+ Cotton Bedding & Linen styles",
      "Hand & Bath Towels",
    ],
  },
  {
    name: "Premium Kit",
    price: "£379",
    description:
      "Enhanced starter bundle with premium bedding, 600gsm towels, bath mat, and upgraded cookware.",
    features: [
      "Superior 12pc Utensil Set",
      "Premium Pan Set",
      "Hand & Bath Towels",
      "4 x Person Porcelain Dining Set",
      "Premium Bedding & Linens upgrade",
    ],
    isPopular: true,
  },
  {
    name: "Deluxe Kit",
    price: "£519",
    description:
      "Enhanced starter bundle with premium bedding, 600gsm towels, bath mat, and upgraded cookware.",
    features: [
      "Deluxe 4pc Pan Set",
      "15 Utensil Set",
      "Hand & Bath Towels",
      "4 x Person Porcelain Dining Set",
      "Premium Bedding & Linens upgrade",
    ],
  },
];

export const KIT_CATEGORIES: KitCategory[] = [
  {
    name: "Kitchen Kits",
    description:
      "Induction-compatible cookware, durable dinnerware, and utensils — everything to cook and entertain from day one.",
    fromPrice: "£109",
    ctaLabel: "Explore Kitchen Kits",
  },
  {
    name: "Bedding Kits",
    description:
      "Hotel-quality duvets, pillows, and linens in a range of styles — your sanctuary of comfort sorted.",
    fromPrice: "£59",
    ctaLabel: "Explore Bedding Kits",
  },
  {
    name: "Bathroom Kits",
    description:
      "Hotel-grade towels, wash kits & accessories — upgrade to a luxury bathroom experience.",
    fromPrice: "£32",
    ctaLabel: "Explore Bathroom Kits",
  },
];

export const KIT_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    stepNumber: 1,
    title: "Browse & Pick Your Kit",
    description: "Choose from Complete, Kitchen, Bedding, or Bathroom kits.",
    iconPath: "check-rate",
  },
  {
    stepNumber: 2,
    title: "Customise Your Options",
    description: "Select your bedding size, colour, and crockery preference in just a few clicks.",
    iconPath: "fill-details",
  },
  {
    stepNumber: 3,
    title: "We Deliver To Your Door",
    description:
      "Sit back — your kit arrives at your home or halls on a weekday, with a minimum of 5 days for delivery.",
    iconPath: "receive",
  },
];

export const KIT_WHY_CHOOSE: WhyChooseFeature[] = [
  {
    iconPath: "kit-stored",
    subtitle: "Online orders can't arrive before you move in",
    title: "amber's partnership keep your kit stored",
    description: "amber's partnership keep your kit stored",
  },
  {
    iconPath: "delivery",
    subtitle: "Tens of items, days of shopping",
    title: "One box, delivered to your accommodation",
    description: "One box, delivered to your accommodation",
  },
  {
    iconPath: "quick",
    subtitle: "Empty room on arrival",
    title: "Bed, kitchen & bathroom setup in an hour",
    description: "Bed, kitchen & bathroom setup in an hour",
  },
];

export const KIT_WHY_CHOOSE_TITLE = "Why choose amber essentials kit?";

export const KIT_FAQS: FaqItem[] = [
  {
    question: "What's included in a Complete Kit?",
    answer:
      "The Complete Kit includes bedroom essentials (bedding, pillow, duvet), kitchen kit (cookware, utensils, dinnerware), and bathroom kit (towels, bath mat, accessories).",
  },
  {
    question: "Can I customise my kit?",
    answer:
      "Yes! You can choose bedding size, colour preferences, and crockery style during checkout.",
  },
  {
    question: "When should I order my kit?",
    answer:
      "Delivery is weekdays only and takes a minimum of 5 days. We recommend ordering at least a week before your move-in date.",
  },
  {
    question: "Do you deliver to university halls?",
    answer: "Yes, we deliver directly to university halls and private accommodation across the UK.",
  },
  {
    question: "What if I already have some items?",
    answer:
      "You can purchase individual category kits — Kitchen, Bedding, or Bathroom — instead of the full Complete Kit.",
  },
  {
    question: "Who fulfils and delivers the kits?",
    answer: "Amber UniKit is fulfilled by our trusted partners and delivered via tracked courier.",
  },
];

export const KIT_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Ordered my Complete Kit a week before moving in and everything arrived perfectly packed. The bedding quality is genuinely brilliant — felt like home from day one!",
    name: "Priya Sharma",
    university: "University of Edinburgh",
  },
  {
    quote:
      "I was so stressed about what to bring to halls. The Complete Kit sorted literally everything — bedroom, kitchen, bathroom — in one order. Absolute lifesaver.",
    name: "Ciarán Murphy",
    university: "University College Dublin",
  },
  {
    quote:
      "The customisation is great. I picked my bedding colour and size, and it all turned up two days later. Couldn't have been smoother.",
    name: "Emily Johnson",
    university: "University of Manchester",
  },
  {
    quote:
      "Honestly the best thing I did before starting uni. My flatmates were all scrambling around IKEA on move-in day and I just unpacked my kit. Worth every penny.",
    name: "Aoife Kelly",
    university: "Trinity College Dublin",
  },
];

export interface FlightCoupon {
  discount: string; // e.g. "7%* off"
  validText: string; // e.g. "*Valid on every flight"
  code: string; // e.g. "MMTAMBER"
}

// TODO(partner — PRD Section 12 claims gate): confirm the exact promo-code string,
// contracted discount %, and any min-spend / route eligibility with MakeMyTrip
// before publish. `code` below is a PLACEHOLDER and must not go live unconfirmed.
// Under the MMT handoff model this is a single stackable % code (no dollar bands).
export const CROSS_FLIGHTS_COUPONS: FlightCoupon[] = [
  {
    discount: "7%* off",
    validText: "*Valid on every flight",
    code: "MMTAMBER", // PLACEHOLDER — confirm with MMT
  },
];

export const FLIGHTS_COUPON_DISCLAIMER =
  "Discount applies when the amber code is entered at MakeMyTrip checkout. Savings are subject to MakeMyTrip's terms and eligible routes.";

export const FLIGHTS_HERO_HEADING = {
  prefix: "Flat ",
  highlight: "7%*\nOff",
  suffix: " On Flight",
};

export const FLIGHTS_HERO_HEADING_TABLET = "Flat 7%* Off On Flight";

export const FLIGHTS_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    stepNumber: 1,
    title: "Copy your amber code",
    description: "Grab the exclusive promo code above with one tap.",
    iconPath: "copy-code",
  },
  {
    stepNumber: 2,
    title: "Book on MakeMyTrip",
    description:
      'Head to MakeMyTrip, choose your flight, and paste the code in the "Offers & Promo Codes" field at checkout.',
    iconPath: "book-mmt",
  },
  {
    stepNumber: 3,
    title: "Save instantly",
    description: "Your discount applies before payment. Complete your booking and you're set.",
    iconPath: "save-instant",
  },
];

// Persistent hero note (Figma 6212-19189 bottom bar). Communicates the offer is
// open to all students — no amber housing booking needed.
export const FLIGHTS_HERO_BADGE_NOTE =
  "*No amber booking required. Any student can use this code, even without an amber housing booking.";

export const FLIGHTS_HERO_DESCRIPTION =
  "Pre-book your student flights to your new city- and every leg in between.\nExclusive savings on trusted travel.";

export const FLIGHTS_HERO_DESCRIPTION_MWEB =
  "Pre-book your student flights to your new city- and every leg in between.";
export const FLIGHTS_HERO_CTA_LABEL = "Book on MakeMyTrip";
// TODO(partner — PRD Section 12): confirm the MakeMyTrip landing URL and the exact
// amber UTM structure before publish. Placeholder below routes to MMT flights with
// amber UTMs for attribution only (pure promo-code handoff — no affiliate mechanic).
export const FLIGHTS_HERO_CTA_URL =
  "https://www.makemytrip.com/flights/?utm_source=amber&utm_medium=partnership&utm_campaign=amber_mmt_flights";

export const FLIGHTS_WHY_CHOOSE_SUBTITLE =
  "Book flights across a global airline network — with exclusive student savings and flexible rebooking.";

export const FLIGHTS_FEATURE_IMAGE_URL =
  "https://prod-static-assets.amberstudent.com/images/amberplus-whychoose.png";
export const FLIGHTS_FEATURE_PANEL_BG =
  "linear-gradient(141deg, rgba(151, 197, 190, 0) 50%, rgba(151, 197, 190, 1) 98%), " +
  "radial-gradient(circle at 51% -4950%, rgba(38, 166, 149, 0.5) 9%, rgba(38, 166, 149, 0) 100%), " +
  "#e1f4f3";

export const BANK_HERO_HEADING = {
  prefix: "Get Upto ",
  highlight: "£40*", // rendered in red
  suffix: " when you land in UK",
};
export const BANK_HERO_DESCRIPTION =
  "Open Revolut on arrival. Fully digital - no branch visits, no\nqueues, no UK credit history needed.";
export const BANK_HERO_CTA_LABEL = "Claim £40 Now";
export const BANK_HERO_CTA_URL =
  "https://www.revolut.com/promo/aff-spendxgety-affiliates/?irclickid=SxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0&irgwc=1&afsrc=1&p=i0&ext=6396629&RA=&af_c_id=6396629&pid=impactradius_int&af_click_lookback=30d&gwlurl=https%3A%2F%2Fwww.revolut.com%2Fpromo%2Faff-spendxgety-affiliates%2F%3Firclickid%3DSxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0&irgwc=1&afsrc=1&p=i0&ext=6396629&RA=&af_media_type=%7Bif%28propertytype&idfa=%7Bappleifa%7D&advertising_id=%7Bgoogleaid%7D&af_siteid=6396629&af_sub_siteid=Amber+Internet+Solutions+Inc.&secondary_publisher=6396629&c=9626&click_id=SxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0&clickid=SxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0&clickid=SxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0&clickid=SxL1UEwHWxyZUHbVQYylR3XKUkuy5O2eEzMhVQ0";

export interface BankHeroStat {
  iconKey: "money" | "globe" | "users";
  value: string;
  label: string;
}
export const BANK_HERO_STATS: BankHeroStat[] = [
  { iconKey: "money", value: "£40", label: "Total Reward" },
  { iconKey: "globe", value: "30+", label: "Currencies" },
  { iconKey: "users", value: "70M+", label: "Consumers" },
];

// Dark disclaimer bar sitting below the bank hero card (#1F2937 bg, white text)
export const BANK_HERO_DISCLAIMER =
  "*You can't open a Revolut account from India - sign-up requires a UK address. Save the amber link now and open the app on\nDay 1 in the UK using your accommodation address. That's when your £40 unlocks.";

export const BANK_WHY_CHOOSE_TITLE = "Why Choose Revolut";
export const BANK_WHY_CHOOSE_SUBTITLE =
  "Join the UK with a bank account that over 70 million people trust — and start off with £40 in your pocket!";
export const BANK_WHY_CHOOSE_SUBTITLE_MWEB =
  "Land in the UK with the bank account 70M+ people already trust — and £40 in your pocket.";

export const BANK_WHY_CHOOSE: WhyChooseFeature[] = [
  {
    iconPath: "bank-digital",
    title: "100% Digital",
    description: "Open on your phone. No branch visits, no UK credit history, no queues.",
  },
  {
    iconPath: "bank-customers",
    title: "70M+ Customers",
    description: "Trusted globally — students moving to 30+ countries.",
  },
  {
    iconPath: "bank-fca",
    title: "FCA Authorised",
    description:
      "Revolut Ltd authorised under the Electronic Money Regulations 2011 (FRN: 900562).",
  },
  {
    iconPath: "bank-stack",
    title: "£40 Welcome Stack",
    description: "£20 amber reward and £20 Revolut bonus, stackable.",
  },
];

export const BANK_FEATURE_IMAGE_URL =
  "https://prod-static-assets.amberstudent.com/images/amber_plus_cross_banks_revoult_feature_image.svg";

export const BANK_FEATURE_IMAGE_MWEB_URL =
  "https://prod-static-assets.amberstudent.com/images/amber_plus_cross_banks_revoult_feature_image_mweb.svg";
export const BANK_FEATURE_PANEL_BG =
  "linear-gradient(180deg, rgba(15, 49, 255, 1) 0%, rgba(114, 142, 254, 1) 50%, rgba(183, 206, 255, 1) 100%)";

export const BANK_FAQ_SUBTITLE = "Everything you need to know before claiming your £40.";

export const REVOLUT_FAQS: FaqItem[] = [
  {
    question: "How do I unlock the full £40 reward?",
    answer:
      "Two rewards stack to make £40. £20 from amber credits once your sign-up via the amber referral link is verified. £20 Revolut welcome bonus credits after any £20 purchase made on your Revolut card within 30 days of activation.",
  },
  {
    question: "Can I open a Revolut account before I land in the UK?",
    answer:
      "No. Revolut sign-up requires a valid UK residential address — you can't open the account from India or any country outside the UK. Save the amber link now and open the app on Day 1 in the UK using your accommodation address. That's when your £40 unlocks.",
  },
  {
    question: "What UK address do I use for sign up?",
    answer:
      "Your UK accommodation address — student halls or your private rental. Once you have that, you can also claim your @revtag and order a physical card mailed to that address. (Your virtual card is ready instantly.)",
  },
  {
    question: "How long does verification take?",
    answer:
      "Verification is usually approved within minutes. You'll need a photo of your passport and a quick selfie taken in the app.",
  },
  {
    question: "Do I need a UK credit history?",
    answer:
      "No. Revolut is fully digital and built for people just arriving in the UK — no UK credit history, no branch visits, no queues. You can open the app on the day you arrive.",
  },
  {
    question: "Is Revolut a real bank?",
    answer:
      "Revolut Ltd is authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 (FRN: 900562). It's trusted by 70M+ customers globally, including students moving to 30+ countries.",
  },
  {
    question: "What if I miss the 30-day window for the £20 bonus?",
    answer:
      "The amber £20 still credits once your sign-up is verified — that part doesn't expire. The £20 Revolut welcome bonus needs any £20 card purchase within 30 days of activating your card to unlock.",
  },
  {
    question: "What currency are the rewards paid in?",
    answer: "Both rewards are paid in GBP, straight into your Revolut account.",
  },
];

export const FLIGHTS_FAQ_SUBTITLE =
  "Everything you need to know before booking through amber × MakeMyTrip.";

// NOTE(PRD Section 12): every starred (*) claim below — 7% rate, stacking on student
// fares & bank offers, extra baggage, 450+ airlines / named carriers, and
// zero-cancellation protection — must be confirmed with MakeMyTrip before publish.
export const MMT_FAQS: FaqItem[] = [
  {
    question: "How does the amber × MakeMyTrip code work?",
    answer:
      'Copy the exclusive code from this page, head to MakeMyTrip, select your flight, and paste it in the "Offers & Promo Codes" field at checkout to apply your discount.',
  },
  {
    question: "Can I stack the code with student fares?",
    answer:
      "Yes. The discount stacks on top of MMT's eligible student fares and baggage benefits — you don't have to choose one over the other.",
  },
  {
    question: "Does it work for international and domestic flights?",
    answer: "Both — for flights to your university abroad and for eligible domestic connections.",
  },
  {
    question: "Do I need an amber accommodation booking to use this code?",
    answer:
      "No. The offer is open to all students moving abroad; no existing amber housing booking is required.",
  },
  {
    question: "What documents do I need for student fares?",
    answer:
      "Requirements vary by airline. Student fares and baggage benefits may require a valid Student ID, university acceptance letter, or student visa at check-in — check your carrier's student-fare terms before you travel.",
  },
  {
    question: "Where do I enter the code on MakeMyTrip?",
    answer:
      'On the MMT flight payment screen, find the "Offers & Promo Codes" section, paste your amber code, and click "Apply".',
  },
];

// iconPath keys are retained as-is; the matching MakeMyTrip icons/artwork are a
// follow-up once Figma is delivered by the design team.
export const MMT_WHY_CHOOSE: WhyChooseFeature[] = [
  {
    iconPath: "kiwi-guarantee",
    title: "Extra Baggage for Students",
    description:
      "Fly stress-free with student fares that include up to 10–15kg* of extra baggage on international routes.",
  },
  {
    iconPath: "kiwi-countries",
    title: "Global Airline Partnerships",
    description:
      "Access direct and connecting flights across 450+* international airlines including Emirates, Qatar Airways, British Airways, and Indigo.",
  },
  {
    iconPath: "kiwi-discount",
    title: "Instant 7% Off Every Fare",
    description:
      "Save up to 7%* extra on your ticket. Stack the exclusive amber code on top of eligible student fares and bank offers.",
  },
  {
    iconPath: "kiwi-transport",
    title: "Flexible Booking & Rescheduling",
    description:
      "Low-cost date changes, zero-cancellation protection options, and easy refund tracking through MMT.",
  },
];

export const EXCHANGE_RATE_API_PRIMARY = (code: string) =>
  `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${code.toLowerCase()}.json`;

export const EXCHANGE_RATE_API_FALLBACK = (code: string) =>
  `https://latest.currency-api.pages.dev/v1/currencies/${code.toLowerCase()}.json`;

export const RATE_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
